import { useState, useEffect, useCallback } from 'react';
import { getModuleCounts } from '../services/activitiesService';
import { getUnreadCount as getUnreadMessages } from '../services/messagesService';
import { getUnreadCount as getUnreadNotifications } from '../services/notificationsService';
import { useAuth } from '../context/AuthContext';

/**
 * Hook that provides module counts for sidebar/home badges.
 * Combines 4 sources:
 * 1. API counts (from module_count_rules table) for 18+ modules
 * 2. Contacts count from localStorage
 * 3. Unread messages count from messagesService
 * 4. Unread notifications count from notificationsService
 *
 * @returns {Object} { AGENDA: 5, CONTACTS: 42, MESSAGES: 3, NOTIFICATIONS: 1, ... }
 */
export const useModuleCounts = () => {
    const [counts, setCounts] = useState({});
    const { user } = useAuth();

    const loadCounts = useCallback(async () => {
        const peopleId = user?.id;
        const peopleUuid = user?.peopleUuid || '';

        if (!peopleId) return;

        try {
            // 1. API counts (18 modules from DB rules)
            const apiCounts = await getModuleCounts(peopleId, peopleUuid);

            // 2. Contacts from localStorage
            let contactsCount = 0;
            try {
                const contacts = JSON.parse(localStorage.getItem('cabaza_user_contacts') || '[]');
                contactsCount = Array.isArray(contacts) ? contacts.length : 0;
            } catch (e) {
                contactsCount = 0;
            }

            // 3. Unread messages
            const unreadMessages = await getUnreadMessages();

            // 4. Unread notifications
            const unreadNotifications = await getUnreadNotifications(peopleId);

            // Normalize API counts keys (ColdFusion may return uppercase)
            const normalized = {};
            for (const [key, value] of Object.entries(apiCounts)) {
                normalized[key.toUpperCase()] = value;
            }

            setCounts({
                ...normalized,
                CONTACTS: contactsCount,
                MESSAGES: unreadMessages,
                NOTIFICATIONS: unreadNotifications
            });
        } catch (error) {
            console.error('Error loading module counts:', error);
        }
    }, [user]);

    useEffect(() => {
        loadCounts();
        const interval = setInterval(loadCounts, 60000);
        return () => clearInterval(interval);
    }, [loadCounts]);

    return counts;
};
